import * as Animatable from 'react-native-animatable';
import Label from 'Label';
import React, {useEffect, useReducer} from 'react';
import SkillzImage from 'SkillzImage';
import {AnimatedCircularProgress} from 'react-native-circular-progress';

import styles, {
  BACKGROUND_COLOR,
  TINT_COLOR,
  CircleProgressLevelColors,
  INNER_CIRCLE_BORDER_COLOR,
  styleOfLevelCircle,
  styleOfLevelCircleAnimatableView,
  styleOfLevelCircleImage,
  styleOfLevelCircleLabel,
  styleOfProgressCircle,
  DEFAULT_SIZE,
  overrideStyleOfInnerCircle,
} from './CircleProgressLevel.style';

import {View, Easing} from 'react-native';

export const TEST_IDS = {
  CIRCLE_PROGRESS_LEVEL: 'circle_progress_level',
  CIRCLE_PROGRESS_LEVEL_INNER_CIRCLE_VIEW: 'inner_circle_view',
  CIRCLE_PROGRESS_LEVEL_INNER_CIRCLE_IMAGE: 'inner_circle_image',
  CIRCLE_PROGRESS_LEVEL_LABEL_CIRCLE_VIEW: 'label_circle_view',
  CIRCLE_PROGRESS_LEVEL_LABEL_CIRCLE_IMAGE: 'label_circle_image',
  CIRCLE_PROGRESS_LEVEL_LABEL_CIRCLE_TEXT: 'label_circle_text',
};

const DEFAULT_INITIAL_PREFILL = 0;
const DEFAULT_FINAL_FILL = 100;
const DEFAULT_ANIMATION_STARTING_POINT = 180;
const PROGRESS_BAR_WIDTH = 6;
//exported for test purpose
export const BAR_DURATION = 3000; //650;
const NUMBER_DURATION = 500;
const NO_LEVEL_INDEX = -1;

type Level = {
  /** Either a number to indicate the level, or a string for the image of the specific level.*/
  level: number | string;
  /** Percentage accomplished during that level */
  percentage: number;
};

export interface ICircleProgressLevelProps {
  /** For test purpose */
  testID?: string;
  /** Color of the meter and the inner circle background */
  colors?: CircleProgressLevelColors;
  /** To know if the view is animated, false by default */
  animated?: boolean;
  /** Size of the whole component */
  size?: number;
  /** If undefined, component is a placeholder */
  levels?: Level[];
  /** Either call the function to render the center image, or take a string for the center image source */
  centerImage?: ((height: number, width: number) => React.ReactElement) | string;
  /** Override background color of the inner circle */
  backgroundColorInnerCircle?: string;
  /** Override border color of the inner circle */
  innerCircleBorderColor?: string;
}

type State = {
  levelIndex: number;
  animation: boolean;
  prefill: number;
  fill: number;
};

// eslint-disable-next-line no-shadow
enum EActionType {
  RESET,
  LEVEL_UP_INIT,
  LEVEL_UP_PLAY,
}

type Action = {
  type: EActionType;
  levels?: Level[];
  animated?: boolean;
};

const reducer = (state: State, action: Action): State => {
  if (action.type === EActionType.RESET) {
    return initState({animated: action.animated, levels: action.levels});
  }
  if (!action.levels || state.levelIndex === NO_LEVEL_INDEX) {
    return state;
  }
  switch (action.type) {
    case EActionType.LEVEL_UP_INIT: {
      const levelIndex = Math.min(action.levels.length - 1, state.levelIndex + 1); //limit to the size of the levels array
      if (levelIndex === state.levelIndex) {
        //if level hasn't changed
        return state;
      } else {
        return {
          ...state,
          levelIndex,
          animation: false,
          prefill: DEFAULT_INITIAL_PREFILL,
          fill: DEFAULT_INITIAL_PREFILL,
        };
      }
    }
    case EActionType.LEVEL_UP_PLAY: {
      if (state.levelIndex === action.levels.length - 1) {
        //if at the last level
        return {
          ...state,
          animation: true,
          fill: action.levels[state.levelIndex].percentage,
        };
      } else {
        return {
          ...state,
          animation: true,
          fill: DEFAULT_FINAL_FILL,
        };
      }
    }
    /* istanbul ignore next */ //ignore test coverage on this return as we never call dispatch with other type
    default:
      return state;
  }
};

const initState = ({
  animated,
  levels,
}: Pick<ICircleProgressLevelProps, 'animated' | 'levels'>): State => {
  if (!levels || levels.length === 0) {
    //placeholder
    return {
      levelIndex: NO_LEVEL_INDEX,
      animation: false,
      prefill: DEFAULT_INITIAL_PREFILL,
      fill: DEFAULT_INITIAL_PREFILL,
    };
  }

  if (!animated) {
    //no animation, go directly to the last level
    const levelIndex = levels.length - 1;
    const percentage = levels[levelIndex].percentage;
    return {
      levelIndex,
      animation: false,
      prefill: percentage,
      fill: percentage,
    };
  }

  if (levels.length === 1) {
    //if only one level, we start by 0 to animate to the current level
    const levelIndex = 0;
    return {
      levelIndex,
      animation: true,
      prefill: DEFAULT_INITIAL_PREFILL,
      fill: levels[levelIndex].percentage,
    };
  } else {
    //if more than one, we start from the first percentage to 100 and continue unitl last level
    const levelIndex = 0;
    return {
      levelIndex,
      animation: true,
      prefill: levels[levelIndex].percentage,
      fill: DEFAULT_FINAL_FILL,
    };
  }
};

export const CircleProgressLevel = ({
  testID,
  colors = {
    backgroundColor: BACKGROUND_COLOR,
    tintColor: TINT_COLOR,
    innerCircleBorderColor: INNER_CIRCLE_BORDER_COLOR,
  },
  animated = false,
  size = DEFAULT_SIZE,
  levels,
  centerImage,
  backgroundColorInnerCircle,
  innerCircleBorderColor,
}: ICircleProgressLevelProps) => {
  const [state, dispach] = useReducer(reducer, {animated, levels}, initState);

  useEffect(() => {
    dispach({type: EActionType.RESET, levels, animated});
  }, [animated, levels]);

  const isPlaceholder = () => state.levelIndex === NO_LEVEL_INDEX;

  const renderIcon = () => {
    if (!centerImage) {
      return null;
    }

    const sizeOfImage = size / 2;
    if (typeof centerImage === 'string') {
      return (
        <View testID={TEST_IDS.CIRCLE_PROGRESS_LEVEL_INNER_CIRCLE_IMAGE} style={styles.iconImg}>
          <SkillzImage
            source={centerImage}
            style={[styles.iconImg, {width: sizeOfImage, height: sizeOfImage}]}
          />
        </View>
      );
    } else {
      return centerImage(sizeOfImage, sizeOfImage);
    }
  };

  const renderInnerCircle = () => {
    return (
      <View
        testID={TEST_IDS.CIRCLE_PROGRESS_LEVEL_INNER_CIRCLE_VIEW}
        style={[
          styleOfProgressCircle(size, colors),
          styles.innerCircle,
          overrideStyleOfInnerCircle(backgroundColorInnerCircle, innerCircleBorderColor),
        ]}
      >
        {renderIcon()}
      </View>
    );
  };

  const onCircularAnimationComplete = () => {
    if (!animated) {
      //we should not trigger any animation related stuff if the component is not animated by props
      return;
    }
    if (state.animation) {
      dispach({type: EActionType.LEVEL_UP_INIT, levels});
    } else {
      dispach({type: EActionType.LEVEL_UP_PLAY, levels});
    }
  };

  const renderProgressCircle = () => {
    return (
      <AnimatedCircularProgress
        duration={state.animation ? BAR_DURATION : 0}
        size={size}
        width={PROGRESS_BAR_WIDTH}
        prefill={state.prefill}
        fill={state.fill}
        rotation={DEFAULT_ANIMATION_STARTING_POINT}
        tintColor={colors.tintColor}
        backgroundColor={colors.backgroundColor}
        lineCap={isPlaceholder() ? 'butt' : 'round'}
        onAnimationComplete={onCircularAnimationComplete}
        easing={Easing.bezier(0.24, 0.42, 0.19, 1)}
      >
        {(_fill) => {
          return renderInnerCircle();
        }}
      </AnimatedCircularProgress>
    );
  };

  const renderLevelText = (levelToRender: number) => {
    const currentLeveltext = (
      <Label
        testID={TEST_IDS.CIRCLE_PROGRESS_LEVEL_LABEL_CIRCLE_TEXT}
        style={[styleOfLevelCircleLabel(size), styles.currentLevelText]}
        adjustsFontSizeToFit={true}
        numberOfLines={1}
      >
        {levelToRender.toString()}
      </Label>
    );
    if (animated) {
      return (
        <Animatable.View
          animation={state.animation ? 'fadeInUp' : undefined}
          duration={NUMBER_DURATION}
          useNativeDriver={true}
          easing={'ease-in-out'}
        >
          {currentLeveltext}
        </Animatable.View>
      );
    }
    return currentLeveltext;
  };

  const renderLevelCircle = () => {
    const level = levels && levels[state.levelIndex] && levels[state.levelIndex].level;

    const levelCircle = (
      <View
        testID={TEST_IDS.CIRCLE_PROGRESS_LEVEL_LABEL_CIRCLE_VIEW}
        style={[styleOfLevelCircle(size, isPlaceholder(), colors), styles.levelCircle]}
      >
        {typeof level === 'string' ? (
          <View testID={TEST_IDS.CIRCLE_PROGRESS_LEVEL_LABEL_CIRCLE_IMAGE}>
            <SkillzImage
              style={styleOfLevelCircleImage(size)}
              source={level}
              resizeMode={'contain'}
            />
          </View>
        ) : typeof level === 'number' ? (
          renderLevelText(level)
        ) : null}
      </View>
    );

    if (animated) {
      return (
        <Animatable.View
          animation={state.animation ? 'pulse' : undefined}
          duration={NUMBER_DURATION}
          useNativeDriver={true}
          easing={'ease-in-out'}
          style={[styleOfLevelCircleAnimatableView(size), styles.levelCircle]}
        >
          {levelCircle}
        </Animatable.View>
      );
    } else {
      return levelCircle;
    }
  };

  return (
    <View testID={testID || TEST_IDS.CIRCLE_PROGRESS_LEVEL} style={styles.mainContainer}>
      {renderProgressCircle()}
      {renderLevelCircle()}
    </View>
  );
};

export default CircleProgressLevel;
